import React, { useEffect } from 'react';

const SEOOptimizer = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  siteName = 'Gadkot - Maharashtra Fort Explorer',
  author = 'Gadkot Team',
  locale = 'en_US',
  fort = null,
  additionalData = {}
}) => {
  // Update document head with SEO meta tags
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || '');
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = description || '';
      document.head.appendChild(metaDescription);
    }
    
    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords || '');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = keywords || '';
      document.head.appendChild(metaKeywords);
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', url || '');
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url || '';
      document.head.appendChild(canonical);
    }
    
    // Update Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: siteName },
      { property: 'og:locale', content: locale }
    ];
    
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (metaTag) {
        metaTag.setAttribute('content', tag.content || '');
      } else {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        metaTag.content = tag.content || '';
        document.head.appendChild(metaTag);
      }
    });
    
    // Update Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
      { name: 'twitter:site', content: '@gadkot' },
      { name: 'twitter:creator', content: '@gadkot' }
    ];
    
    twitterTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (metaTag) {
        metaTag.setAttribute('content', tag.content || '');
      } else {
        metaTag = document.createElement('meta');
        metaTag.name = tag.name;
        metaTag.content = tag.content || '';
        document.head.appendChild(metaTag);
      }
    });
    
    // Add structured data
    const structuredData = generateStructuredData();
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }
    
    // Cleanup function
    return () => {
      // Clean up dynamically created meta tags if needed
    };
  }, [title, description, keywords, image, url, type, siteName, locale, fort, additionalData]);

  // Generate structured data for forts
  const generateStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "description": description,
      "url": url,
      "inLanguage": locale,
      "isPartOf": {
        "@type": "WebSite",
        "name": siteName,
        "url": "https://gadkot.com"
      }
    };
    
    if (fort) {
      baseData.mainEntity = {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        "name": fort.name,
        "description": fort.description || `Historical fort in ${fort.location}`,
        "url": url,
        "image": image,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": fort.location?.split(',')[0] || '',
          "addressRegion": "Maharashtra",
          "addressCountry": "IN"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": fort.coordinates?.lat || 18.5204,
          "longitude": fort.coordinates?.lng || 73.8567
        },
        "aggregateRating": fort.rating ? {
          "@type": "AggregateRating",
          "ratingValue": fort.rating,
          "reviewCount": fort.reviews || 100,
          "bestRating": 5,
          "worstRating": 1
        } : undefined,
        "openingHours": fort.openingHours || "Mo-Su 09:00-18:00",
        "touristType": ["Historical Site", "Cultural Heritage", "Architecture", "Trekking"],
        "isAccessibleForFree": fort.ticketPrice ? fort.ticketPrice === 0 : true,
        "publicAccess": true
      };
    }
    
    return baseData;
  };

  // This component doesn't render anything visible
  return null;
};

// SEO Hook for dynamic optimization
export const useSEO = (seoData) => {
  useEffect(() => {
    // Update page title
    if (seoData.title) {
      document.title = seoData.title;
    }
    
    // Update meta description
    if (seoData.description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', seoData.description);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = seoData.description;
        document.head.appendChild(meta);
      }
    }
    
    // Update canonical URL
    if (seoData.url) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', seoData.url);
      } else {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = seoData.url;
        document.head.appendChild(canonical);
      }
    }
  }, [seoData]);
};

// Local SEO Component
export const LocalSEO = ({ fort }) => {
  useEffect(() => {
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": fort.name,
      "description": fort.description,
      "image": fort.image,
      "url": fort.url,
      "telephone": fort.phone || "+91-20-12345678",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": fort.address,
        "addressLocality": fort.location,
        "addressRegion": "Maharashtra",
        "postalCode": fort.postalCode,
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": fort.coordinates?.lat,
        "longitude": fort.coordinates?.lng
      },
      "openingHours": fort.openingHours || [
        "Mo-Su 09:00-18:00"
      ],
      "priceRange": fort.ticketPrice ? `₹${fort.ticketPrice}` : "Free",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": fort.rating,
        "reviewCount": fort.reviews || 100
      }
    };

    let localBusinessScript = document.querySelector('script[type="application/ld+json"][data-local="true"]');
    if (localBusinessScript) {
      localBusinessScript.textContent = JSON.stringify(localBusinessData);
    } else {
      localBusinessScript = document.createElement('script');
      localBusinessScript.type = 'application/ld+json';
      localBusinessScript.setAttribute('data-local', 'true');
      localBusinessScript.textContent = JSON.stringify(localBusinessData);
      document.head.appendChild(localBusinessScript);
    }

    return () => {
      if (localBusinessScript) {
        localBusinessScript.remove();
      }
    };
  }, [fort]);

  return null;
};

// Article SEO Component
export const ArticleSEO = ({ article }) => {
  useEffect(() => {
    const articleData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "image": article.image,
      "author": {
        "@type": "Person",
        "name": article.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Gadkot",
        "logo": {
          "@type": "ImageObject",
          "url": "https://gadkot.com/logo.png"
        }
      },
      "datePublished": article.publishedDate,
      "dateModified": article.modifiedDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": article.url
      }
    };

    let articleScript = document.querySelector('script[type="application/ld+json"][data-article="true"]');
    if (articleScript) {
      articleScript.textContent = JSON.stringify(articleData);
    } else {
      articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.setAttribute('data-article', 'true');
      articleScript.textContent = JSON.stringify(articleData);
      document.head.appendChild(articleScript);
    }

    return () => {
      if (articleScript) {
        articleScript.remove();
      }
    };
  }, [article]);

  return null;
};

export default SEOOptimizer;
